export type UppFloorPlanElementKind =
  | 'table'
  | 'bar'
  | 'furniture'
  | 'architecture'
  | 'vertex';

/**
 * Integrator-facing bag on each floor-plan element: custom payload, primary and secondary labels.
 * `label` / `sublabel` are drawn in a **badge** when non-empty (rounded rect + **bold** text): the badge’s **bottom-left** is placed at the **screen top-right** corner of the label reference rectangle (after element rotation), then offset **30 %** of width toward screen left and **30 %** of height toward screen down (overlap into the shape); inset distances use the same reference size as the bbox (tables **with chairs** use an expanded bbox). The badge **counter-rotates** with view **`screenRotationDeg`** so text stays upright; `sublabel` uses a smaller type size; `custom` is not rendered.
 * **`attention`**: when true on a **table**, the tabletop and chair strokes use the theme palette for {@link UppFloorPlanElementOptions.attentionTone}.
 */
import type { UppKonvaAttention } from '../../model/upp-konva-types';
import { floorPlanElementUuid } from '../internal/floor-plan-uuid';

function normalizeAttentionTone(raw: unknown): UppKonvaAttention {
  if (raw === 'SUCCESS' || raw === 'WARNING' || raw === 'DANGER') {
    return raw;
  }
  return 'WARNING';
}

export interface UppFloorPlanElementOptions {
  /** Integrator payload; default **`null`**. */
  custom: any;
  label: string;
  sublabel: string;
  /** Highlight state; table colors only when the row is a table. */
  attention: boolean;
  /** Tone of the highlight when `attention` is true on a table (defaults to **WARNING**). */
  attentionTone: UppKonvaAttention;
  /**
   * English comment normalized.
   * Defaults to **true**.
   */
  attentionAnimated: boolean;
}

export function normalizeUppFloorPlanElementOptions(
  partial?: Partial<UppFloorPlanElementOptions>,
): UppFloorPlanElementOptions {
  const o: UppFloorPlanElementOptions = {
    label: partial?.label ?? '',
    sublabel: partial?.sublabel ?? '',
    attention: partial?.attention === true,
    attentionTone: normalizeAttentionTone(partial?.attentionTone),
    attentionAnimated: partial?.attentionAnimated !== false,
    custom: null,
  };
  if (partial !== undefined && Object.prototype.hasOwnProperty.call(partial, 'custom')) {
    o.custom = partial.custom as any;
  }
  return o;
}

/**
 * Floor-plan element in **cm** and degrees (logical room space).
 * Product equivalent: plan element / master-plan `UppPlanoElemento` concept.
 */
export abstract class UppFloorPlanElement {
  selected = false;

  /**
   * Stack order for painting and marquee hit-testing: **lower** values are drawn **under** higher ones.
   * When equal, legacy rules apply (tables before draw pieces before perimeter markers among ties at `0`).
   */
  zIndex = 0;

  /** Options bag; mutate or use {@link custom}, {@link label}, {@link sublabel} accessors. */
  readonly options: UppFloorPlanElementOptions;

  /** Stable UUID v4; set at construction (pass a v4 string, or a legacy seed string mapped deterministically). */
  readonly uuid: string;

  protected constructor(
    uuidOrSeed: string,
    readonly kind: UppFloorPlanElementKind,
    public xCm: number,
    public yCm: number,
    public widthCm: number,
    public heightCm: number,
    public rotationDeg: number,
    elementOptions?: Partial<UppFloorPlanElementOptions>,
  ) {
    this.uuid = floorPlanElementUuid(uuidOrSeed);
    this.options = normalizeUppFloorPlanElementOptions(elementOptions);
  }

  get custom(): any {
    return this.options.custom;
  }

  set custom(value: any) {
    this.options.custom = value === undefined ? null : value;
  }

  get label(): string {
    return this.options.label;
  }

  set label(value: string) {
    this.options.label = value ?? '';
  }

  get sublabel(): string {
    return this.options.sublabel;
  }

  set sublabel(value: string) {
    this.options.sublabel = value ?? '';
  }

  get attention(): boolean {
    return this.options.attention === true;
  }

  set attention(value: boolean) {
    this.options.attention = value === true;
  }

  get attentionTone(): UppKonvaAttention {
    return this.options.attentionTone;
  }

  set attentionTone(value: UppKonvaAttention) {
    this.options.attentionTone = normalizeAttentionTone(value);
  }

  get attentionAnimated(): boolean {
    return this.options.attentionAnimated !== false;
  }

  set attentionAnimated(value: boolean) {
    this.options.attentionAnimated = value === true;
  }
}

/**
 * Table footprint: rectangle (v3 `SQUARE`) or ellipse in the bbox (v3 `CIRCLE`; circle if width ≈ height).
 */
export type UppFloorPlanTableShape = 'RECT' | 'ELLIPSE';

export class UppFloorPlanTable extends UppFloorPlanElement {
  /**
   * Chairs around the table (v3 chair sizes). `0` = tabletop only (rectangle or ellipse).
   * **Rect** (`tableShape === 'RECT'`): chairs on four sides (allocation like v3 square table).
   * **Ellipse** (`tableShape === 'ELLIPSE'`): chairs evenly on the ellipse perimeter (v3 circle table).
   */
  readonly chairCount: number;

  /** v3 `QRCODE.draw.shape`: `RECT` ↔ `SQUARE`, `ELLIPSE` ↔ `CIRCLE`. */
  readonly tableShape: UppFloorPlanTableShape;

  /**
   * Occupied diners for seat-fill visualization (not persisted as geometry; integrator may set via `data.diners`).
   * Fills at most {@link chairCount} chair slots clockwise; does not add seats.
   */
  diners = 0;

  constructor(
    uuid: string,
    xCm: number,
    yCm: number,
    widthCm: number,
    heightCm: number,
    rotationDeg: number,
    chairCount = 4,
    tableShape: UppFloorPlanTableShape = 'RECT',
    elementOptions?: Partial<UppFloorPlanElementOptions>,
  ) {
    super(uuid, 'table', xCm, yCm, widthCm, heightCm, rotationDeg, elementOptions);
    this.chairCount = chairCount;
    this.tableShape = tableShape;
  }
}

export type UppFloorPlanBarShape = 'LINEAR' | 'TOLEFT' | 'TORIGHT' | 'USHAPE';

export class UppFloorPlanBar extends UppFloorPlanElement {
  /**
   * Stools on the left side of the gap (local view, Y-axis pointing down).
   * English comment normalized.
   */
  readonly stoolLeft?: number;
  /**
   * Stools at the front (bottom edge of bounding box, client side).
   * English comment normalized.
   */
  readonly stoolFront?: number;
  /**
   * English comment normalized.
   */
  readonly stoolRight?: number;

  constructor(
    uuid: string,
    xCm: number,
    yCm: number,
    widthCm: number,
    heightCm: number,
    rotationDeg: number,
    readonly barShape: UppFloorPlanBarShape = 'LINEAR',
    stoolLeft?: number,
    stoolFront?: number,
    stoolRight?: number,
    elementOptions?: Partial<UppFloorPlanElementOptions>,
  ) {
    super(uuid, 'bar', xCm, yCm, widthCm, heightCm, rotationDeg, elementOptions);
    this.stoolLeft = stoolLeft;
    this.stoolFront = stoolFront;
    this.stoolRight = stoolRight;
  }
}

export type UppFloorPlanGenericShape = 'GENRCT' | 'GENRND';

/** Layout for `furnitureClass === 'sofa'` (v3-style L vs straight run). Ignored for other classes. */
export type UppFloorPlanSofaShape = 'LINEAR' | 'TOLEFT' | 'TORIGHT';

export class UppFloorPlanFurniture extends UppFloorPlanElement {
  /** For `furnitureClass === 'generic'`: rectangle vs ellipse (`DRAWITEM.shape`). */
  readonly genericShape: UppFloorPlanGenericShape;

  /** For `furnitureClass === 'sofa'`: straight or L return on the left/right of the bbox (local space). */
  readonly sofaShape: UppFloorPlanSofaShape;

  /**
   * English comment normalized.
   *   if no options, pass `undefined` as eighth argument before the shape (e.g. `…, 'sofa', undefined, undefined, 'TOLEFT')`).
   */
  constructor(
    uuid: string,
    xCm: number,
    yCm: number,
    widthCm: number,
    heightCm: number,
    rotationDeg: number,
    readonly furnitureClass: 'billiard' | 'football' | 'sofa' | 'generic' = 'generic',
    genericShape?: UppFloorPlanGenericShape,
    elementOptions?: Partial<UppFloorPlanElementOptions>,
    sofaShape?: UppFloorPlanSofaShape,
  ) {
    super(uuid, 'furniture', xCm, yCm, widthCm, heightCm, rotationDeg, elementOptions);
    this.genericShape =
      genericShape ?? (furnitureClass === 'generic' ? 'GENRCT' : 'GENRCT');
    this.sofaShape = sofaShape ?? 'LINEAR';
  }
}

export class UppFloorPlanArchitecture extends UppFloorPlanElement {
  /**
   * Subtype for rendering: in row **`UppKonvaShape`** the key is **`architectureShape`**.
   * Values that rendering recognizes (any other is normalized to the class default):
   * **door** — `TOLEFT` | `TORIGHT`; **window** — `SINGLE` | `DOUBLE`; **stairs** — `SIMPLE` | `DOUBLE` | `SNAIL`.
   * Defaults: door `TOLEFT`, window `SINGLE`, stairs `SIMPLE` (aligned with v3).
   */
  readonly architectureShape: string;

  constructor(
    uuid: string,
    xCm: number,
    yCm: number,
    widthCm: number,
    heightCm: number,
    rotationDeg: number,
    readonly architectureClass: 'door' | 'window' | 'stairs' = 'door',
    architectureShape?: string,
    elementOptions?: Partial<UppFloorPlanElementOptions>,
  ) {
    super(uuid, 'architecture', xCm, yCm, widthCm, heightCm, rotationDeg, elementOptions);
    this.architectureShape =
      architectureShape ??
      (architectureClass === 'door'
        ? 'TOLEFT'
        : architectureClass === 'window'
          ? 'SINGLE'
          : 'SIMPLE');
  }
}

export class UppFloorPlanVertex extends UppFloorPlanElement {
  constructor(
    uuid: string,
    xCm: number,
    yCm: number,
    /** Order along the interior-wall chain within {@link polygon}. */
    public sort: number,
    widthCm = 40,
    heightCm = 40,
    rotationDeg = 0,
    elementOptions?: Partial<UppFloorPlanElementOptions>,
    /** Vertices sharing the same {@link polygon} form one chain; order by {@link sort} within the chain. */
    public polygon = 'default',
    /** When true (for all vertices of the same {@link polygon}), the chain is drawn closed with a uniform fill. */
    public closed = false,
  ) {
    super(uuid, 'vertex', xCm, yCm, widthCm, heightCm, rotationDeg, elementOptions);
  }
}
